import { getLoggedinUser } from "./api_helper";

// Single source of truth for client-side auth validity.
//
// The app stores the whole login response under localStorage "authUser", with
// the JWT on its `.token` property. Historically the app treated "a token
// exists" as "the user is authenticated" and only discovered expiry when a
// background request came back 401 - which let the authorised UI flash on
// screen for a moment before the redirect to /login. These helpers add a
// synchronous expiry check so an expired token is treated as logged-out before
// any protected UI renders.

// Clock skew allowance (seconds) so we don't log a user out on a token that is
// only a hair past expiry versus the local clock.
const CLOCK_SKEW_SECONDS = 10;

// localStorage keys cleared when we drop a dead session. Mirrors the keys the
// central 401 handler (helpers/APIdata/fetchAPI.js) already removes.
const AUTH_STORAGE_KEYS = ["authUser", "firebase_fcm_token"];

// Decode the payload segment of a JWT without any dependency. base64url ->
// JSON. Returns null for anything that isn't a decodable JWT.
const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // pad to a multiple of 4 for atob
    while (base64.length % 4) base64 += "=";
    return JSON.parse(atob(base64));
  } catch (_) {
    return null;
  }
};

// Is this token provably expired?
//
// FAIL-SAFE: if the token is missing, not a decodable JWT, or carries no `exp`
// claim, we return false ("not provably expired") and rely on the server's 401
// as the backstop. This guarantees we never force-log-out a user holding a
// token whose format we don't understand.
export const isTokenExpired = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  const nowSeconds = Date.now() / 1000;
  return payload.exp + CLOCK_SKEW_SECONDS < nowSeconds;
};

// Which company does this token actually address?
//
// The backend picks the company database purely from the JWT's
// Company_IdExternal claim - nothing else in the request selects a tenant. So
// the token is the single source of truth for "which company am I looking at",
// and anything that writes a token must keep it in step with the company the
// UI is displaying (authUser.get_company_data). Returns null for a token we
// cannot decode, so callers treat "unknown" as "do not act".
export const getTokenCompanyId = (token) => {
  const payload = decodeJwtPayload(token);
  return payload && payload.Company_IdExternal ? payload.Company_IdExternal : null;
};

// Clear the stored session. Kept here so every "token is dead" path clears the
// same keys the 401 handler does.
export const clearStoredAuth = () => {
  try {
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (_) {
    // ignore storage access errors
  }
};

// Return the logged-in user only when a token is present AND not expired.
// Otherwise clear the stale session and return null. Use this anywhere the app
// previously read the raw "authUser" to decide "am I logged in".
export const getValidLoggedinUser = () => {
  const user = getLoggedinUser();
  const token = user && user.token;
  if (!token || isTokenExpired(token)) {
    if (user) clearStoredAuth();
    return null;
  }
  return user;
};
